import { Component, ElementRef, ViewChild, AfterViewInit, OnInit, OnDestroy } from '@angular/core';
import * as maplibregl from 'maplibre-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { RoadProjectService, Project, Section, Segment, Subsegment, NumberingRule, ProjectStats } from '../shared/services/road-project.service';
import { Subject, takeUntil } from 'rxjs';

enum ProjectViewMode {
  Projects,
  Sections,
  Segments,
  Subsegments,
  Rules,
  Conflicts
}

enum DrawingMode {
  None,
  Section,
  Segment,
  Subsegment
}

@Component({
  selector: 'app-road-project',
  templateUrl: './road-project.component.html',
  styleUrls: ['./road-project.component.css']
})
export class RoadProjectComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('mapContainer') mapContainer!: ElementRef;
  
  // Map and drawing
  map!: maplibregl.Map;
  draw!: MapboxDraw;
  
  // Data
  projects: Project[] = [];
  sections: Section[] = [];
  segments: Segment[] = [];
  subsegments: Subsegment[] = [];
  numberingRules: NumberingRule[] = [];
  projectStats: ProjectStats | null = null;
  
  // Current selections
  currentProject: Project | null = null;
  currentSection: Section | null = null;
  currentSegment: Segment | null = null;
  selectedFeature: any = null;
  
  // UI state
  viewMode = ProjectViewMode.Projects;
  drawingMode = DrawingMode.None;
  showLayerManager = false;
  showFeaturePanel = false;
  showNumberingPanel = false;
  showConflictPanel = false;
  isDrawing = false;
  
  // Form data
  newProject: Partial<Project> = {};
  newSection: Partial<Section> = {};
  newSegment: Partial<Segment> = {};
  newSubsegment: Partial<Subsegment> = {};
  newRule: Partial<NumberingRule> = {};
  
  // Display settings
  displayOffsets = {
    sections: 0,
    segments: 0,
    subsegments: 0
  };
  
  // Auto-numbering settings
  autoNumberingConfig = {
    enabled: true,
    prefix: 'AUTO',
    startNumber: 1,
    increment: 1,
    direction: 'asc' as 'asc' | 'desc'
  };
  
  // Destroy subject for cleanup
  private destroy$ = new Subject<void>();

  constructor(private roadProjectService: RoadProjectService) {}

  ngOnInit(): void {
    this.loadProjects();
    this.loadNumberingRules();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.initializeMap();
  }

  // Map initialization
  initializeMap(): void {
    this.map = new maplibregl.Map({
      container: this.mapContainer.nativeElement,
      style: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
      center: [78.9629, 20.5937],
      zoom: 4
    });

    this.map.on('load', () => {
      this.draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          line_string: true,
          polygon: false,
          point: false,
          trash: true
        },
        styles: this.getCustomDrawStyles()
      });

      this.map.addControl(this.draw as unknown as maplibregl.IControl, 'top-left');

      // Drawing event handlers
      this.map.on('draw.create', this.onDrawCreate.bind(this));
      this.map.on('draw.update', this.onDrawUpdate.bind(this));
      this.map.on('draw.delete', this.onDrawDelete.bind(this));
      
      // Click handler for feature selection
      this.map.on('click', this.onMapClick.bind(this));
    });
  }

  // Data loading methods
  loadProjects(): void {
    this.roadProjectService.getProjects()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.projects = response.data;
        },
        error: (error) => {
          console.error('Error loading projects:', error);
        }
      });
  }

  loadSections(projectId: string): void {
    this.roadProjectService.getSections(projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.sections = response.data.features.map((f: any) => f.properties);
          this.addSectionsToMap(response.data);
        },
        error: (error) => {
          console.error('Error loading sections:', error);
        }
      });
  }

  loadSegments(sectionId: number): void {
    this.roadProjectService.getSegments(sectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.segments = response.data.features.map((f: any) => f.properties);
          this.addSegmentsToMap(response.data);
        },
        error: (error) => {
          console.error('Error loading segments:', error);
        }
      });
  }

  loadSubsegments(segmentId: number): void {
    this.roadProjectService.getSubsegments(segmentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.subsegments = response.data.features.map((f: any) => f.properties);
          this.addSubsegmentsToMap(response.data);
        },
        error: (error) => {
          console.error('Error loading subsegments:', error);
        }
      });
  }

  loadNumberingRules(): void {
    this.roadProjectService.getNumberingRules()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.numberingRules = response.data;
        },
        error: (error) => {
          console.error('Error loading numbering rules:', error);
        }
      });
  }

  loadProjectStats(projectId: string): void {
    this.roadProjectService.getProjectStats(projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.projectStats = response.data;
        },
        error: (error) => {
          console.error('Error loading project stats:', error);
        }
      });
  }

  // Map layer management
  addSectionsToMap(geojson: any): void {
    const sourceId = 'sections-source';
    const layerId = 'sections-layer';

    this.removeLayerIfExists(sourceId, layerId);

    this.map.addSource(sourceId, {
      type: 'geojson',
      data: geojson
    });

    this.map.addLayer({
      id: layerId,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': '#ff0000',
        'line-width': 4,
        'line-opacity': 0.8
      }
    });
  }

  addSegmentsToMap(geojson: any): void {
    const sourceId = 'segments-source';
    const layerId = 'segments-layer';

    this.removeLayerIfExists(sourceId, layerId);

    this.map.addSource(sourceId, {
      type: 'geojson',
      data: geojson
    });

    this.map.addLayer({
      id: layerId,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': '#00ff00',
        'line-width': 3,
        'line-opacity': 0.8
      }
    });
  }

  addSubsegmentsToMap(geojson: any): void {
    const sourceId = 'subsegments-source';
    const layerId = 'subsegments-layer';

    this.removeLayerIfExists(sourceId, layerId);

    this.map.addSource(sourceId, {
      type: 'geojson',
      data: geojson
    });

    this.map.addLayer({
      id: layerId,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': '#0000ff',
        'line-width': 2,
        'line-opacity': 0.8
      }
    });
  }

  removeLayerIfExists(sourceId: string, layerId: string): void {
    if (this.map.getLayer(layerId)) {
      this.map.removeLayer(layerId);
    }
    if (this.map.getSource(sourceId)) {
      this.map.removeSource(sourceId);
    }
  }

  // Drawing methods
  startDrawing(mode: DrawingMode): void {
    this.drawingMode = mode;
    this.isDrawing = true;

    switch (mode) {
      case DrawingMode.Section:
        this.draw.changeMode('draw_line_string');
        break;
      case DrawingMode.Segment:
        this.draw.changeMode('draw_line_string');
        break;
      case DrawingMode.Subsegment:
        this.draw.changeMode('draw_line_string');
        break;
    }
  }

  stopDrawing(): void {
    this.drawingMode = DrawingMode.None;
    this.isDrawing = false;
    this.draw.changeMode('simple_select');
  }

  onDrawCreate(event: any): void {
    const feature = event.features[0];
    this.handleDrawnFeature(feature);
  }

  onDrawUpdate(event: any): void {
    console.log('Feature updated:', event);
  }

  onDrawDelete(event: any): void {
    console.log('Feature deleted:', event);
  }

  onMapClick(event: any): void {
    const features = this.map.queryRenderedFeatures(event.point);
    if (features.length > 0) {
      this.selectedFeature = features[0];
    }
  }

  handleDrawnFeature(feature: any): void {
    switch (this.drawingMode) {
      case DrawingMode.Section:
        this.createSectionFromDrawing(feature);
        break;
      case DrawingMode.Segment:
        this.createSegmentFromDrawing(feature);
        break;
      case DrawingMode.Subsegment:
        this.createSubsegmentFromDrawing(feature);
        break;
    }
  }

  // Creation methods
  createSectionFromDrawing(feature: any): void {
    if (!this.currentProject) {
      alert('Please select a project first');
      return;
    }

    const sectionName = prompt('Enter section name:');
    if (!sectionName) return;

    const startChainage = parseFloat(prompt('Enter start chainage:') || '0');
    const endChainage = parseFloat(prompt('Enter end chainage:') || '1000');

    const sectionData: Partial<Section> = {
      section_name: sectionName,
      project_id: this.currentProject.project_code,
      start_chainage: startChainage,
      end_chainage: endChainage,
      geometry: feature.geometry,
      properties: {}
    };

    this.roadProjectService.createSection(sectionData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('Section created:', response);
          this.draw.deleteAll();
          this.loadSections(this.currentProject!.project_code);
        },
        error: (error) => {
          console.error('Error creating section:', error);
        }
      });
  }

  createSegmentFromDrawing(feature: any): void {
    if (!this.currentSection) {
      alert('Please select a section first');
      return;
    }

    const segmentName = prompt('Enter segment name:');
    if (!segmentName) return;

    const startChainage = parseFloat(prompt('Enter start chainage:') || '0');
    const endChainage = parseFloat(prompt('Enter end chainage:') || '500');

    const segmentData: Partial<Segment> = {
      segment_name: segmentName,
      section_id: this.currentSection.id,
      start_chainage: startChainage,
      end_chainage: endChainage,
      geometry: feature.geometry,
      properties: {},
      display_offset: this.displayOffsets.segments
    };

    this.roadProjectService.createSegment(segmentData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('Segment created:', response);
          this.draw.deleteAll();
          this.loadSegments(this.currentSection!.id);
        },
        error: (error) => {
          console.error('Error creating segment:', error);
        }
      });
  }

  createSubsegmentFromDrawing(feature: any): void {
    if (!this.currentSegment) {
      alert('Please select a segment first');
      return;
    }

    const subsegmentName = prompt('Enter subsegment name:');
    if (!subsegmentName) return;

    const startChainage = parseFloat(prompt('Enter start chainage:') || '0');
    const endChainage = parseFloat(prompt('Enter end chainage:') || '100');

    const subsegmentData: Partial<Subsegment> = {
      subsegment_name: subsegmentName,
      segment_id: this.currentSegment.id,
      section_id: this.currentSection!.id,
      start_chainage: startChainage,
      end_chainage: endChainage,
      geometry: feature.geometry,
      properties: {},
      display_offset: this.displayOffsets.subsegments,
      rule_based: false
    };

    this.roadProjectService.createSubsegment(subsegmentData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('Subsegment created:', response);
          this.draw.deleteAll();
          this.loadSubsegments(this.currentSegment!.id);
        },
        error: (error) => {
          console.error('Error creating subsegment:', error);
        }
      });
  }

  // Project management
  createProject(): void {
    if (!this.newProject.project_code || !this.newProject.project_name) {
      alert('Project code and name are required');
      return;
    }

    this.roadProjectService.createProject(this.newProject)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('Project created:', response);
          this.newProject = {};
          this.loadProjects();
        },
        error: (error) => {
          console.error('Error creating project:', error);
        }
      });
  }

  selectProject(project: Project): void {
    this.currentProject = project;
    this.currentSection = null;
    this.currentSegment = null;
    this.viewMode = ProjectViewMode.Sections;
    this.loadSections(project.project_code);
    this.loadProjectStats(project.project_code);
  }

  selectSection(section: Section): void {
    this.currentSection = section;
    this.currentSegment = null;
    this.viewMode = ProjectViewMode.Segments;
    this.loadSegments(section.id);
  }

  selectSegment(segment: Segment): void {
    this.currentSegment = segment;
    this.viewMode = ProjectViewMode.Subsegments;
    this.loadSubsegments(segment.id);
  }

  // Auto-numbering methods
  applyAutoNumbering(): void {
    if (!this.autoNumberingConfig.enabled) return;

    const rule: Partial<NumberingRule> = {
      rule_name: 'Auto Numbering',
      rule_type: 'segment',
      prefix: this.autoNumberingConfig.prefix,
      start_number: this.autoNumberingConfig.startNumber,
      increment: this.autoNumberingConfig.increment,
      direction: this.autoNumberingConfig.direction,
      format_pattern: '{number:03d}'
    };

    this.roadProjectService.createNumberingRule(rule)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('Numbering rule created:', response);
          this.loadNumberingRules();
        },
        error: (error) => {
          console.error('Error creating numbering rule:', error);
        }
      });
  }

  applySubsegmentRules(): void {
    if (!this.currentSegment) {
      alert('Please select a segment first');
      return;
    }

    this.roadProjectService.applySubsegmentRules(this.currentSegment.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('Subsegment rules applied:', response);
          this.loadSubsegments(this.currentSegment!.id);
        },
        error: (error) => {
          console.error('Error applying subsegment rules:', error);
        }
      });
  }

  // Segment splitting
  splitSegmentAtPoint(): void {
    if (!this.currentSegment) {
      alert('Please select a segment first');
      return;
    }

    // Get click point from map
    this.map.once('click', (event) => {
      const splitPoint = {
        type: 'Point',
        coordinates: [event.lngLat.lng, event.lngLat.lat]
      };

      this.roadProjectService.splitSegment(this.currentSegment!.id, splitPoint)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            console.log('Segment split:', response);
            this.loadSegments(this.currentSection!.id);
          },
          error: (error) => {
            console.error('Error splitting segment:', error);
          }
        });
    });
  }

  // Display offset management
  updateDisplayOffset(type: 'segment' | 'subsegment', id: number, offset: number): void {
    this.roadProjectService.updateDisplayOffset(id, type, offset)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('Display offset updated:', response);
          // Refresh the appropriate layer
          if (type === 'segment' && this.currentSection) {
            this.loadSegments(this.currentSection.id);
          } else if (type === 'subsegment' && this.currentSegment) {
            this.loadSubsegments(this.currentSegment.id);
          }
        },
        error: (error) => {
          console.error('Error updating display offset:', error);
        }
      });
  }

  // UI methods
  toggleLayerManager(): void {
    this.showLayerManager = !this.showLayerManager;
  }

  toggleFeaturePanel(): void {
    this.showFeaturePanel = !this.showFeaturePanel;
  }

  toggleNumberingPanel(): void {
    this.showNumberingPanel = !this.showNumberingPanel;
  }

  toggleConflictPanel(): void {
    this.showConflictPanel = !this.showConflictPanel;
  }

  setViewMode(mode: ProjectViewMode): void {
    this.viewMode = mode;
  }

  // Utility methods
  getCustomDrawStyles() {
    return [
      {
        id: 'gl-draw-line-inactive',
        type: 'line',
        filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
        paint: {
          'line-color': '#fbb03b',
          'line-width': 2
        }
      }
    ];
  }

  // Navigation
  backToProjects(): void {
    this.viewMode = ProjectViewMode.Projects;
    this.currentProject = null;
    this.currentSection = null;
    this.currentSegment = null;
  }

  backToSections(): void {
    this.viewMode = ProjectViewMode.Sections;
    this.currentSection = null;
    this.currentSegment = null;
  }

  backToSegments(): void {
    this.viewMode = ProjectViewMode.Segments;
    this.currentSegment = null;
  }

  // Layer visibility management
  toggleLayerVisibility(layerId: string, event: any): void {
    const visibility = event.target.checked ? 'visible' : 'none';
    if (this.map.getLayer(layerId)) {
      this.map.setLayoutProperty(layerId, 'visibility', visibility);
    }
  }

  // Conflict detection
  detectConflicts(): void {
    this.roadProjectService.detectConflicts()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('Conflicts detected:', response);
          // Handle conflict display
        },
        error: (error) => {
          console.error('Error detecting conflicts:', error);
        }
      });
  }
}
